/**
 * TTS queue: parse notes text into QueueItems (speak, highlight, click, pause, emoji, etc.).
 * Pure parsing and formatting; no DOM or speech. Easy to unit test.
 */

export const CODEWORD_REGEX = /\[\[([^\]]+)\]\]/g;
export const NAME_PLACEHOLDER_REGEX = /\u200B__TTS_NAME__(?::([^\u200B]*))?\u200B/g;

export type QueueItem =
  | { type: 'speak'; text: string }
  | { type: 'highlight'; selector: string | string[]; ring?: boolean }
  | { type: 'starthighlight'; selector: string | string[] }
  | { type: 'stophighlight'; selector: string | string[] }
  | { type: 'zoom'; selector: string | string[]; ms?: number; level?: number }
  | { type: 'zoomin'; selector: string | string[]; level?: number }
  | { type: 'zoomout' }
  | { type: 'keypress'; combo: string }
  | { type: 'click'; selector: string | string[] }
  | { type: 'focus'; selector: string | string[] }
  | { type: 'pause'; ms: number }
  | { type: 'scroll'; selector: string | string[] }
  | { type: 'waitclick'; selector: string | string[] }
  | { type: 'play'; selector: string | string[] }
  | { type: 'pausevideo'; selector: string | string[] }
  | { type: 'emoji'; value: string }
  | { type: 'type'; selector: string; text: string }
  /** Open chat drawer and focus composer. */
  | { type: 'chatopen' }
  /** Open chat drawer and prefill draft text. */
  | { type: 'chatdraft'; text: string }
  /** Clear current chat draft text (if input exists). */
  | { type: 'chatcleardraft' }
  /** Close chat drawer. */
  | { type: 'chatclose' }
  /** Play one hosted sound; queue waits until playback ends. Only one TTS sound at a time. */
  | { type: 'sound'; key: string; volume?: number }
  /** Play sound without blocking the queue; still exclusive with other TTS sounds. */
  | { type: 'soundasync'; key: string; volume?: number }
  /** Stop the current TTS sound (same as slide change / TTS off). */
  | { type: 'stopsound' }
  /** Fade out current sound over `ms` (default {@link DEFAULT_TTS_FADEOUT_MS}), then stop. */
  | { type: 'fadeoutsound'; ms?: number }
  /** Open Reveal pause screen and start break timer in minutes. */
  | { type: 'pausebreak'; minutes: number }
  /** Open/close Reveal pause screen without timer. */
  | { type: 'pauseon' }
  | { type: 'pauseoff' }
  /** Remote QR overlay on presenter deck (bar button + expand). */
  | { type: 'qrshow' }
  | { type: 'qrhide' }
  | { type: 'qrbig' };

/** Default for `[[fadeoutsound]]` when no `:ms` is given. */
export const DEFAULT_TTS_FADEOUT_MS = 600;

/**
 * Parse payload after `sound:` / `soundasync:` — key, optional `|volume` (0–1), e.g. `applause|0.65`.
 */
export function parseSoundCodewordPayload(value: string): { key: string; volume?: number } {
  const v = value.trim();
  const lastPipe = v.lastIndexOf('|');
  if (lastPipe < 0) return { key: v };
  const tail = v.slice(lastPipe + 1).trim();
  const head = v.slice(0, lastPipe).trim();
  if (!head) return { key: v };
  const vol = parseFloat(tail);
  if (!Number.isFinite(vol) || tail === '') return { key: v };
  if (!/^-?\d*\.?\d+(e[-+]?\d+)?$/i.test(tail)) return { key: v };
  return { key: head, volume: Math.max(0, Math.min(1, vol)) };
}

export function stripMarkdownForTTS(text: string): string {
  if (!text) return '';
  return String(text)
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/^#+\s*(.+)$/gm, '$1')
    .replace(/^\s*---\s*$/gm, '')
    .replace(/\n+/g, ' ');
}

/** Format text as HTML for subtitle (same classes as notes-tts). */
export function formatForSubtitle(text: string): string {
  if (!text) return '';
  const escaped = String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped
    .replace(/^#+\s*(.+)$/gm, '<span class="notes-tts-subtitle-heading">$1</span>')
    .replace(/^\s*---\s*$/gm, '<hr class="notes-tts-subtitle-hr">')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br>');
}

/**
 * Parse the inner part of a single `[[...]]` codeword (same rules as parseNotes).
 * Returns null when the codeword is not recognized or invalid.
 */
export function parseCodewordInner(part: string): QueueItem | null {
  const colon = part.indexOf(':');
  const action = (colon >= 0 ? part.slice(0, colon).trim().toLowerCase() : part.toLowerCase()).trim();
  const value = (colon >= 0 ? part.slice(colon + 1).trim() : '');
  const selectors = value.indexOf(',') >= 0 ? value.split(',').map((s) => s.trim()).filter(Boolean) : value ? [value] : [];
  const selPayload: string | string[] = selectors.length === 0 ? value : selectors.length === 1 ? selectors[0]! : selectors;
  if (action === 'highlight' || action === 'ring') {
    return { type: 'highlight', selector: selPayload, ring: action === 'ring' };
  }
  if (action === 'starthighlight') {
    return { type: 'starthighlight', selector: selPayload };
  }
  if (action === 'stophighlight') {
    return { type: 'stophighlight', selector: selPayload };
  }
  if (action === 'click') {
    return { type: 'click', selector: selPayload };
  }
  if (action === 'focus') {
    return { type: 'focus', selector: selPayload };
  }
  if (action === 'pause') {
    const ms = parseInt(value, 10);
    if (!isNaN(ms) && ms > 0) return { type: 'pause', ms: Math.min(ms, 30000) };
    return null;
  }
  if (action === 'scroll') {
    return { type: 'scroll', selector: selPayload };
  }
  if (action === 'waitclick') {
    return { type: 'waitclick', selector: selPayload };
  }
  if (action === 'play') {
    return { type: 'play', selector: selPayload };
  }
  if (action === 'pausevideo') {
    return { type: 'pausevideo', selector: selPayload };
  }
  if (action === 'zoom' || action === 'autozoom') {
    const zoomParts = value.split('|');
    const selectorRaw = (zoomParts[0] || '').trim();
    const dur = parseInt((zoomParts[1] || '').trim(), 10);
    const level = parseFloat((zoomParts[2] || '').trim());
    const zoomSelectors = selectorRaw.indexOf(',') >= 0
      ? selectorRaw.split(',').map((s) => s.trim()).filter(Boolean)
      : selectorRaw ? [selectorRaw] : [];
    const zoomSelPayload: string | string[] =
      zoomSelectors.length === 0 ? selectorRaw : zoomSelectors.length === 1 ? zoomSelectors[0]! : zoomSelectors;
    return {
      type: 'zoom',
      selector: zoomSelPayload,
      ms: !isNaN(dur) && dur > 0 ? Math.min(dur, 30000) : 2500,
      level: !isNaN(level) && level > 1 ? Math.min(level, 6) : 2
    };
  }
  if (action === 'zoom-in' || action === 'zoomin') {
    const zoomParts = value.split('|');
    const selectorRaw = (zoomParts[0] || '').trim();
    const level = parseFloat((zoomParts[1] || '').trim());
    const zoomSelectors = selectorRaw.indexOf(',') >= 0
      ? selectorRaw.split(',').map((s) => s.trim()).filter(Boolean)
      : selectorRaw ? [selectorRaw] : [];
    const zoomSelPayload: string | string[] =
      zoomSelectors.length === 0 ? selectorRaw : zoomSelectors.length === 1 ? zoomSelectors[0]! : zoomSelectors;
    return {
      type: 'zoomin',
      selector: zoomSelPayload,
      level: !isNaN(level) && level > 1 ? Math.min(level, 6) : 2
    };
  }
  if (action === 'zoom-out' || action === 'zoomout') {
    return { type: 'zoomout' };
  }
  if ((action === 'keypress' || action === 'hotkey' || action === 'keys') && value) {
    return { type: 'keypress', combo: value };
  }
  if (action === 'emoji' && value) {
    return { type: 'emoji', value };
  }
  if (action === 'name') {
    const fallback = value.trim() || '';
    return { type: 'speak', text: '\u200B__TTS_NAME__:' + fallback + '\u200B' };
  }
  if (action === 'type') {
    const pipe = value.indexOf('|');
    if (pipe >= 0) return { type: 'type', selector: value.slice(0, pipe).trim(), text: value.slice(pipe + 1) };
  }
  if (action === 'chatopen') {
    return { type: 'chatopen' };
  }
  if (action === 'chatdraft' && value) {
    return { type: 'chatdraft', text: value };
  }
  if (action === 'chatcleardraft' || action === 'clearchatdraft') {
    return { type: 'chatcleardraft' };
  }
  if (action === 'chatclose' || action === 'closechat') {
    return { type: 'chatclose' };
  }
  if (action === 'sound' && value) {
    const { key, volume } = parseSoundCodewordPayload(value);
    const v =
      volume !== undefined && volume < 1 - 1e-9 ? volume : undefined;
    return v !== undefined ? { type: 'sound', key, volume: v } : { type: 'sound', key };
  }
  if (action === 'soundasync' && value) {
    const { key, volume } = parseSoundCodewordPayload(value);
    const v =
      volume !== undefined && volume < 1 - 1e-9 ? volume : undefined;
    return v !== undefined ? { type: 'soundasync', key, volume: v } : { type: 'soundasync', key };
  }
  if (action === 'stopsound') {
    return { type: 'stopsound' };
  }
  if (action === 'fadeoutsound') {
    const raw = value.trim();
    if (!raw) return { type: 'fadeoutsound', ms: DEFAULT_TTS_FADEOUT_MS };
    const ms = parseInt(raw, 10);
    if (!Number.isFinite(ms) || ms <= 0) return { type: 'fadeoutsound', ms: DEFAULT_TTS_FADEOUT_MS };
    return { type: 'fadeoutsound', ms: Math.min(15000, Math.max(100, ms)) };
  }
  if (action === 'pausebreak' || action === 'break') {
    const mins = parseInt(value.trim(), 10);
    if (!Number.isFinite(mins) || mins <= 0) return null;
    return { type: 'pausebreak', minutes: Math.min(60, Math.max(1, mins)) };
  }
  if (action === 'pauseon' || action === 'pause') {
    return { type: 'pauseon' };
  }
  if (action === 'pauseoff' || action === 'resume' || action === 'unpause') {
    return { type: 'pauseoff' };
  }
  if (action === 'qrshow' || action === 'showqr') {
    return { type: 'qrshow' };
  }
  if (action === 'qrhide' || action === 'hideqr') {
    return { type: 'qrhide' };
  }
  if (action === 'qrbig' || action === 'bigqr' || action === 'qrexpand') {
    return { type: 'qrbig' };
  }
  return null;
}

export type SpeakerNoteSegment =
  | { type: 'text'; text: string }
  | { type: 'codeword'; rawInner: string; item: QueueItem | null };

/** Split slide notes into text + codeword segments (preserves whitespace in text parts). */
export function segmentNotesForSpeakerPanel(text: string): SpeakerNoteSegment[] {
  const segments: SpeakerNoteSegment[] = [];
  let lastIndex = 0;
  let match;
  CODEWORD_REGEX.lastIndex = 0;
  while ((match = CODEWORD_REGEX.exec(text)) !== null) {
    const before = text.slice(lastIndex, match.index);
    if (before) segments.push({ type: 'text', text: before });
    const innerTrimmed = match[1].trim();
    const item = parseCodewordInner(innerTrimmed);
    segments.push({ type: 'codeword', rawInner: match[1], item });
    lastIndex = match.index + match[0].length;
  }
  const after = text.slice(lastIndex);
  if (after) segments.push({ type: 'text', text: after });
  return segments;
}

function selStr(sel: string | string[] | undefined): string {
  if (sel == null) return '';
  return Array.isArray(sel) ? sel.join(', ') : String(sel);
}

function formatSoundCodewordPayload(key: string, volume?: number): string {
  if (volume !== undefined && volume < 1 - 1e-9) {
    return `${key}|${volume}`;
  }
  return key;
}

/** Serialize a queue item back to `[[action:value]]` for RevealNotesTTS.runSingleCommand. */
export function serializeQueueItemToCodeword(item: QueueItem): string | null {
  switch (item.type) {
    case 'highlight':
      return item.ring ? `[[ring:${selStr(item.selector)}]]` : `[[highlight:${selStr(item.selector)}]]`;
    case 'starthighlight':
      return `[[starthighlight:${selStr(item.selector)}]]`;
    case 'stophighlight':
      return `[[stophighlight:${selStr(item.selector)}]]`;
    case 'click':
      return `[[click:${selStr(item.selector)}]]`;
    case 'focus':
      return `[[focus:${selStr(item.selector)}]]`;
    case 'scroll':
      return `[[scroll:${selStr(item.selector)}]]`;
    case 'waitclick':
      return `[[waitclick:${selStr(item.selector)}]]`;
    case 'play':
      return `[[play:${selStr(item.selector)}]]`;
    case 'pausevideo':
      return `[[pausevideo:${selStr(item.selector)}]]`;
    case 'emoji':
      return `[[emoji:${item.value}]]`;
    case 'keypress':
      return `[[keypress:${item.combo}]]`;
    case 'zoom': {
      const s = selStr(item.selector);
      const ms = item.ms ?? 2500;
      const lv = item.level ?? 2;
      return `[[zoom:${s}|${ms}|${lv}]]`;
    }
    case 'zoomin': {
      const s = selStr(item.selector);
      const lv = item.level ?? 2;
      return `[[zoomin:${s}|${lv}]]`;
    }
    case 'zoomout':
      return '[[zoomout]]';
    case 'type':
      return `[[type:${item.selector}|${item.text}]]`;
    case 'chatopen':
      return '[[chatopen]]';
    case 'chatdraft':
      return `[[chatdraft:${item.text}]]`;
    case 'chatcleardraft':
      return '[[chatcleardraft]]';
    case 'chatclose':
      return '[[chatclose]]';
    case 'sound':
      return `[[sound:${formatSoundCodewordPayload(item.key, item.volume)}]]`;
    case 'soundasync':
      return `[[soundasync:${formatSoundCodewordPayload(item.key, item.volume)}]]`;
    case 'stopsound':
      return '[[stopsound]]';
    case 'fadeoutsound':
      return item.ms != null && item.ms !== DEFAULT_TTS_FADEOUT_MS
        ? `[[fadeoutsound:${item.ms}]]`
        : '[[fadeoutsound]]';
    case 'pausebreak':
      return `[[pausebreak:${item.minutes}]]`;
    case 'pauseon':
      return '[[pauseon]]';
    case 'pauseoff':
      return '[[pauseoff]]';
    case 'qrshow':
      return '[[qrshow]]';
    case 'qrhide':
      return '[[qrhide]]';
    case 'qrbig':
      return '[[qrbig]]';
    case 'pause':
    case 'speak':
      return null;
    default:
      return null;
  }
}

export function parseNotes(text: string): QueueItem[] {
  const queue: QueueItem[] = [];
  let lastIndex = 0;
  let match;
  CODEWORD_REGEX.lastIndex = 0;
  while ((match = CODEWORD_REGEX.exec(text)) !== null) {
    const before = text.slice(lastIndex, match.index).trim();
    if (before) queue.push({ type: 'speak', text: before });
    const part = match[1].trim();
    const parsed = parseCodewordInner(part);
    if (parsed) queue.push(parsed);
    lastIndex = match.index + match[0].length;
  }
  const after = text.slice(lastIndex).trim();
  if (after) queue.push({ type: 'speak', text: after });
  return mergeNamePlaceholders(queue);
}

export function mergeNamePlaceholders(queue: QueueItem[]): QueueItem[] {
  const NAME_MARKER = '\u200B__TTS_NAME__';
  const merged: QueueItem[] = [];
  for (let i = 0; i < queue.length; i++) {
    const item = queue[i]!;
    if (item.type === 'speak' && item.text.indexOf(NAME_MARKER) !== -1 && item.text.replace(NAME_PLACEHOLDER_REGEX, '').trim() === '') {
      const namePlaceholder = item.text;
      const prev = merged.length > 0 ? merged[merged.length - 1] : null;
      const next = queue[i + 1];
      const prevT = prev?.type === 'speak' ? (prev.text || '') : '';
      const nextT = next?.type === 'speak' ? (next.text || '') : '';
      const spaceBefore = prevT.length > 0 && !/\s$/.test(prevT) ? ' ' : '';
      const spaceAfter = nextT.length > 0 && !/^[\s.,;:!?]/.test(nextT) ? ' ' : '';
      if (prev?.type === 'speak' && next?.type === 'speak') {
        (prev as { text: string }).text = prevT + spaceBefore + namePlaceholder + spaceAfter + nextT;
        i++;
      } else if (prev?.type === 'speak') {
        (prev as { text: string }).text = prevT + spaceBefore + namePlaceholder;
      } else if (next?.type === 'speak') {
        merged.push({ type: 'speak', text: namePlaceholder + spaceAfter + nextT });
        i++;
      } else {
        merged.push({ type: 'speak', text: namePlaceholder });
      }
    } else {
      merged.push(item);
    }
  }
  return merged;
}
